import type { User } from "firebase/auth";

// Partner referral codes. The marketing site links here with ?ref=<code>; the code is kept until sign-up, then attached to the new account.
const API = "https://api.deafassistant.com";
const KEY = "cc_ref";
const MAX_AGE_MS = 180 * 24 * 60 * 60 * 1000;

export const isValidReferralCode = (code: string | null | undefined): code is string =>
  !!code && /^[A-Za-z0-9_-]{2,40}$/.test(code);

function codeFromUrl(): string | null {
  // HashRouter: the code can be before the "#" (from the website) or inside the hash route.
  const fromSearch = new URLSearchParams(window.location.search).get("ref");
  if (isValidReferralCode(fromSearch)) return fromSearch;
  const hash = window.location.hash;
  const q = hash.indexOf("?");
  const fromHash = q >= 0 ? new URLSearchParams(hash.substring(q)).get("ref") : null;
  return isValidReferralCode(fromHash) ? fromHash : null;
}

function codeFromCookie(): string | null {
  const m = document.cookie.match(/(?:^|; )cc_ref=([^;]*)/);
  const code = m ? decodeURIComponent(m[1]) : null;
  return isValidReferralCode(code) ? code : null;
}

/** Call once at startup. A code in the URL replaces a stored one (the most recent partner link wins). */
export function captureReferral(): void {
  try {
    const code = codeFromUrl();
    if (code) localStorage.setItem(KEY, JSON.stringify({ c: code, t: Date.now() }));
  } catch {
    /* storage blocked: the cookie fallback in getReferral still works */
  }
}

export function getReferral(): string | null {
  try {
    const stored = JSON.parse(localStorage.getItem(KEY) || "null");
    if (stored && isValidReferralCode(stored.c) && Date.now() - stored.t < MAX_AGE_MS) return stored.c;
  } catch {
    /* ignore */
  }
  return codeFromUrl() ?? codeFromCookie();
}

export function clearReferral(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

export async function authed(user: User, path: string, init: RequestInit = {}): Promise<Response> {
  const token = await user.getIdToken();
  return fetch(`${API}${path}`, {
    ...init,
    headers: { ...(init.headers || {}), Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
  });
}

/** Never throws: a bad or missing code must not get in the way of creating an account. The API reads the uid and email from the login token. */
export async function attachReferral(user: User): Promise<void> {
  const code = getReferral();
  if (!code) return;
  try {
    const res = await authed(user, "/referral/attach", { method: "POST", body: JSON.stringify({ code, name: user.displayName ?? "" }) });
    // Keep the code on an error (for example the API not updated yet) so it isn't lost.
    if (res.ok) clearReferral();
  } catch {
    /* leave the code stored; nothing else depends on it */
  }
}

export interface MyReferral {
  status: "none" | "pending" | "approved" | "suspended";
  isAdmin: boolean;
  code?: string | null;
  partnerType?: string;
  /** Which emails the partner gets; each is on unless they turned it off. Only sent for partners. */
  emailPreferences?: EmailPreferences;
  visits?: number;
  signups?: number;
  earned?: number;
}

export interface EmailPreferences {
  /** Their code is changed, or their account is paused or resumed. */
  status: boolean;
  /** A new account signs up with their code. */
  signups: boolean;
  /** Earnings are recorded for a referred account. */
  earnings: boolean;
}

export async function saveEmailPreferences(user: User, preferences: EmailPreferences): Promise<void> {
  const res = await authed(user, "/referral/preferences", { method: "POST", body: JSON.stringify(preferences) });
  if (!res.ok) throw new Error(`referral/preferences ${res.status}`);
}

export type ReferralEventType = "applied" | "approved" | "code_changed" | "suspended" | "resumed" | "removed" | "signup" | "signup_removed" | "payment";

export interface ReferralStats {
  days: number;
  partners: { pending: number; approved: number; suspended: number };
  signups: number;
  earned: number;
  /** How many times each kind of event happened in the period. */
  inPeriod: Record<ReferralEventType, number>;
  /** One entry per day, oldest first, with a count per event type. */
  series: ({ date: string } & Record<ReferralEventType, number>)[];
  top: { partnerUid: string; code: string | null; company: string; status: string; signups: number; earned: number }[];
  recent: { at: string; type: ReferralEventType; code: string | null; detail: string | null }[];
}

export async function adminFetchStats(user: User, days = 30): Promise<ReferralStats> {
  const res = await authed(user, `/referral/adminstats?days=${days}`);
  if (!res.ok) throw new Error(`referral/adminstats ${res.status}`);
  return res.json();
}

export async function fetchMyReferral(user: User): Promise<MyReferral> {
  const res = await authed(user, "/referral/mine");
  if (!res.ok) throw new Error(`referral/mine ${res.status}`);
  return res.json();
}

export interface PartnerApplication {
  partnerName: string;
  partnerType: string;
  company: string;
  details: string;
}

export async function applyForProgram(user: User, application: PartnerApplication): Promise<void> {
  const res = await authed(user, "/referral/apply", { method: "POST", body: JSON.stringify(application) });
  if (!res.ok) throw new Error(`referral/apply ${res.status}`);
}

export interface AdminPartner {
  partnerUid: string;
  status: "pending" | "approved" | "suspended";
  code: string | null;
  partnerName: string;
  partnerEmail: string;
  partnerType: string;
  company: string;
  details: string;
  createdAt: string;
  visits: number;
  signups: number;
  earned: number;
}

export async function adminListPartners(user: User): Promise<AdminPartner[]> {
  const res = await authed(user, "/referral/adminlist");
  if (!res.ok) throw new Error(`referral/adminlist ${res.status}`);
  return res.json();
}

/** Resolves with an error message from the API (for example a code that is taken), or null on success. */
export async function adminApprovePartner(user: User, partnerUid: string, code: string): Promise<string | null> {
  const res = await authed(user, "/referral/adminapprove", { method: "POST", body: JSON.stringify({ partnerUid, code }) });
  if (res.ok) return null;
  try {
    return (await res.json()).error ?? `Request failed (${res.status})`;
  } catch {
    return `Request failed (${res.status})`;
  }
}

export async function adminRemovePartner(user: User, partnerUid: string): Promise<void> {
  const res = await authed(user, "/referral/adminremove", { method: "POST", body: JSON.stringify({ partnerUid }) });
  if (!res.ok) throw new Error(`referral/adminremove ${res.status}`);
}

/** The partner kit (texts, videos, QR code) on the website, opened with the partner's own code. Only approved partners are shown the link. */
export const partnerKitLink = (code: string) => `https://conferencecaptioning.com/partners/kit/?code=${encodeURIComponent(code)}`;

export const referralLink = (code: string) => `https://conferencecaptioning.com/?ref=${encodeURIComponent(code)}`;

export interface ReferredAccount {
  /** Only sent to admins; it identifies the row to mark paid. */
  referredUid?: string | null;
  name: string | null;
  email: string | null;
  streamName: string | null;
  signedUpAt: string;
  accountDeleted: boolean;
  deletedAt: string | null;
  status: "demo" | "paid";
  /** US dollars the partner earned from this account, across all its payments. */
  earned: number;
  /** Admin only: the individual commission payments (first event or year, then later ones earning the ongoing 5%). */
  payments?: ReferralPayment[] | null;
}

export interface ReferralPayment {
  id: number;
  amount: number;
  note: string | null;
  paidAt: string;
}

export async function fetchSignups(user: User): Promise<ReferredAccount[]> {
  const res = await authed(user, "/referral/signups");
  if (!res.ok) throw new Error(`referral/signups ${res.status}`);
  return res.json();
}

export async function adminFetchSignups(user: User, partnerUid: string): Promise<ReferredAccount[]> {
  const res = await authed(user, `/referral/adminsignups?partnerUid=${encodeURIComponent(partnerUid)}`);
  if (!res.ok) throw new Error(`referral/adminsignups ${res.status}`);
  return res.json();
}

/** Resolves with an error message from the API, or null on success. */
export async function adminSetOutcome(user: User, referredUid: string, outcome: "demo" | "paid", earned: number): Promise<string | null> {
  const res = await authed(user, "/referral/adminsetoutcome", { method: "POST", body: JSON.stringify({ referredUid, outcome, earned }) });
  if (res.ok) return null;
  try {
    return (await res.json()).error ?? `Request failed (${res.status})`;
  } catch {
    return `Request failed (${res.status})`;
  }
}

export const formatMoney = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD" });

export async function adminRemoveSignup(user: User, referredUid: string): Promise<void> {
  const res = await authed(user, "/referral/adminremovesignup", { method: "POST", body: JSON.stringify({ referredUid }) });
  if (!res.ok) throw new Error(`referral/adminremovesignup ${res.status}`);
}

export async function adminSuspendPartner(user: User, partnerUid: string): Promise<void> {
  const res = await authed(user, "/referral/adminsuspend", { method: "POST", body: JSON.stringify({ partnerUid }) });
  if (!res.ok) throw new Error(`referral/adminsuspend ${res.status}`);
}

/** Resolves with an error message from the API, or null on success. */
export async function adminAddPayment(user: User, referredUid: string, amount: number, note: string): Promise<string | null> {
  const res = await authed(user, "/referral/adminaddpayment", { method: "POST", body: JSON.stringify({ referredUid, amount, note }) });
  if (res.ok) return null;
  try {
    return (await res.json()).error ?? `Request failed (${res.status})`;
  } catch {
    return `Request failed (${res.status})`;
  }
}

export async function adminRemovePayment(user: User, paymentId: number): Promise<void> {
  const res = await authed(user, "/referral/adminremovepayment", { method: "POST", body: JSON.stringify({ paymentId }) });
  if (!res.ok) throw new Error(`referral/adminremovepayment ${res.status}`);
}
